import { useRef, useEffect, useState, ReactNode } from 'react';
import { SelectedIndexData } from '../../../../types';

function findStepElement(container: HTMLElement, stepIndex: number): HTMLElement | null {
  const stepSections = container.querySelectorAll('.step-section');

  for (const element of Array.from(stepSections)) {
    const attr = (element as HTMLElement).getAttribute('data-step-index');
    if (attr !== null && parseInt(attr, 10) === stepIndex) {
      return element as HTMLElement;
    }
  }

  return (stepSections[stepIndex] as HTMLElement) || null;
}

interface UseScrollManagementParams {
  selectedIndexData: SelectedIndexData;
  setSelectedIndexData: (data: SelectedIndexData) => void;
  currentStep: number;
  maxAllowedStepIndex?: number;
  isStepCompleted?: (flowIndex: number) => boolean;
  children: ReactNode;
}

export function useScrollManagement({
  selectedIndexData,
  setSelectedIndexData,
  currentStep,
  maxAllowedStepIndex,
  isStepCompleted,
  children,
}: UseScrollManagementParams) {
  const componentRef = useRef<HTMLDivElement>(null);
  const selectedIndex = +selectedIndexData.value;

  const maxAllowedStepIndexRef = useRef<number | undefined>(maxAllowedStepIndex);
  const currentStepRef = useRef<number>(currentStep);
  const isBlockingScrollRef = useRef<boolean>(false);

  const [nextSelectedStep, setNextSelectedStep] = useState(0);

  useEffect(() => { maxAllowedStepIndexRef.current = maxAllowedStepIndex; }, [maxAllowedStepIndex]);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);

  // Disable pointer events on steps beyond the max clickable step
  useEffect(() => {
    if (!componentRef.current) return;

    const stepSections = componentRef.current.querySelectorAll('.step-section');
    const maxClickable = maxAllowedStepIndex !== undefined ? maxAllowedStepIndex + 1 : currentStep;

    stepSections.forEach((el) => {
      const attr = (el as HTMLElement).getAttribute('data-step-index');
      if (attr !== null) {
        const idx = parseInt(attr, 10);
        (el as HTMLElement).style.pointerEvents = idx > maxClickable ? 'none' : 'auto';
      }
    });
  }, [currentStep, maxAllowedStepIndex, children]);

  // IntersectionObserver: detect which step is most visible during scroll
  useEffect(() => {
    if (!componentRef.current) return;

    const targetElements = componentRef.current.querySelectorAll('.step-section');

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      if (isBlockingScrollRef.current) return;

      let mostVisibleEntry: IntersectionObserverEntry | null = null;
      let highestRatio = 0;

      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
          highestRatio = entry.intersectionRatio;
          mostVisibleEntry = entry;
        }
      }

      if (!mostVisibleEntry) return;

      const targetElement = mostVisibleEntry.target as HTMLElement;
      const stepIndexAttr = targetElement.getAttribute('data-step-index');
      const inScreenIndex = stepIndexAttr !== null
        ? parseInt(stepIndexAttr, 10)
        : Array.from(targetElements).findIndex(e => e === targetElement);

      if (isNaN(inScreenIndex) || inScreenIndex < 0) return;
      if (isBlockingScrollRef.current) return;
      if (
        maxAllowedStepIndexRef.current === undefined &&
        inScreenIndex > currentStepRef.current &&
        currentStepRef.current < 7
      ) return;

      setNextSelectedStep(inScreenIndex);
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: componentRef.current,
      threshold: [0.3, 0.5, 0.8],
    });

    targetElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [children, maxAllowedStepIndex]);

  // Mark scroll origin when user physically scrolls (wheel/touch)
  useEffect(() => {
    const element = componentRef.current;
    if (!element) return;

    const markScrollOrigin = () => {
      if (selectedIndexData.origin !== 'scroll') {
        setSelectedIndexData({ value: selectedIndexData.value, origin: 'scroll' });
      }
    };

    element.addEventListener('wheel', markScrollOrigin, { passive: true });
    element.addEventListener('touchstart', markScrollOrigin, { passive: true });

    return () => {
      element.removeEventListener('wheel', markScrollOrigin);
      element.removeEventListener('touchstart', markScrollOrigin);
    };
  }, [selectedIndexData, setSelectedIndexData, maxAllowedStepIndex, isStepCompleted]);

  // Physical scroll blocking: prevent scrolling beyond current step (non-logged-in flow)
  useEffect(() => {
    const element = componentRef.current;
    if (!element || maxAllowedStepIndex !== undefined) return;

    let scrollTimeout: ReturnType<typeof setTimeout>;
    let isScrollingBack = false;

    const blockAndScrollBack = (
      stepElement: HTMLElement,
      maxAllowedScroll: number,
      stepIndex: number,
    ) => {
      isScrollingBack = true;
      isBlockingScrollRef.current = true;
      setNextSelectedStep(stepIndex);

      const targetScrollTop = stepElement.offsetTop;
      element.scroll({ top: targetScrollTop, behavior: 'smooth' });

      const checkInterval = setInterval(() => {
        if (element.scrollTop > maxAllowedScroll) {
          element.scroll({ top: targetScrollTop, behavior: 'smooth' });
        } else {
          clearInterval(checkInterval);
          isBlockingScrollRef.current = false;
          isScrollingBack = false;
        }
      }, 150);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (element.scrollTop <= maxAllowedScroll) {
          isBlockingScrollRef.current = false;
          isScrollingBack = false;
        }
      }, 2000);
    };

    const handleScroll = () => {
      if (isBlockingScrollRef.current || isScrollingBack) return;

      const step = currentStepRef.current;
      const stepElement = findStepElement(element, step);

      if (stepElement && step < 7) {
        const stepHeight = stepElement.offsetHeight;
        const maxAllowedScroll = stepElement.offsetTop + stepHeight * 0.9;

        if (element.scrollTop > maxAllowedScroll) {
          blockAndScrollBack(stepElement, maxAllowedScroll, step);
        }
      } else if (step === 0) {
        const step0Element = findStepElement(element, 0);
        if (step0Element) {
          const stepHeight = step0Element.offsetHeight;
          const maxAllowedScroll = step0Element.offsetTop + stepHeight * 0.8;

          if (element.scrollTop > maxAllowedScroll) {
            blockAndScrollBack(step0Element, maxAllowedScroll, 0);
          }
        }
      }
    };

    let lastScrollTop = element.scrollTop;
    const throttledHandleScroll = () => {
      const currentScrollTop = element.scrollTop;
      const scrollingDown = currentScrollTop > lastScrollTop;
      lastScrollTop = currentScrollTop;

      if (scrollingDown && !isBlockingScrollRef.current && !isScrollingBack) {
        clearTimeout(scrollTimeout);
        handleScroll();
        scrollTimeout = setTimeout(() => {
          if (!isBlockingScrollRef.current && !isScrollingBack) {
            handleScroll();
          }
        }, 5);
      }
    };

    element.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      clearTimeout(scrollTimeout);
      element.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [currentStep, maxAllowedStepIndex]);

  // Scroll behavior: handle step changes detected by IntersectionObserver
  useEffect(() => {
    if (selectedIndexData.origin !== 'scroll') return;
    if (nextSelectedStep < 0 || isNaN(nextSelectedStep)) return;

    // Logged-in user flow: enforce step boundaries
    if (maxAllowedStepIndex !== undefined) {
      const currentStepCompleted = isStepCompleted ? isStepCompleted(selectedIndex) : true;
      let targetStepIndex: number | undefined = undefined;

      if (nextSelectedStep > maxAllowedStepIndex + 1) {
        targetStepIndex = maxAllowedStepIndex;
      } else if (!currentStepCompleted && nextSelectedStep > selectedIndex) {
        targetStepIndex = selectedIndex;
      }

      if (targetStepIndex !== undefined) {
        setSelectedIndexData({ value: targetStepIndex, origin: 'scroll' });

        requestAnimationFrame(() => {
          if (!componentRef.current) return;
          const targetElement = findStepElement(componentRef.current, targetStepIndex!);
          if (targetElement) {
            componentRef.current.scroll({ top: targetElement.offsetTop, behavior: 'smooth' });
          }
        });
        return;
      }
    }

    // Non-logged-in flow: block forward scrolling beyond current step
    if (
      maxAllowedStepIndex === undefined &&
      ((nextSelectedStep > currentStep && currentStep < 7) || (nextSelectedStep === 0 && selectedIndex === 10))
    ) {
      isBlockingScrollRef.current = true;
      const targetStepForScroll = selectedIndex;
      setNextSelectedStep(targetStepForScroll);

      if (nextSelectedStep !== targetStepForScroll) {
        setSelectedIndexData({ value: targetStepForScroll, origin: 'scroll' });
      }

      requestAnimationFrame(() => {
        if (!componentRef.current) {
          isBlockingScrollRef.current = false;
          return;
        }

        const targetElement = findStepElement(componentRef.current, targetStepForScroll);
        if (targetElement) {
          const scrollTop = targetElement.offsetTop;
          componentRef.current.scrollTop = scrollTop;

          requestAnimationFrame(() => {
            if (componentRef.current) {
              componentRef.current.scroll({ top: scrollTop, behavior: 'smooth' });
            }
          });

          setTimeout(() => {
            isBlockingScrollRef.current = false;
          }, 700);
        } else {
          isBlockingScrollRef.current = false;
        }
      });
      return;
    }

    // Normal update: sync selected index with detected step
    if (nextSelectedStep !== selectedIndex) {
      setSelectedIndexData({ value: nextSelectedStep, origin: 'scroll' });
    }
  }, [nextSelectedStep, currentStep, selectedIndexData.origin, selectedIndex, setSelectedIndexData, maxAllowedStepIndex]);

  // Button click behavior: scroll to selected step when triggered by non-scroll action
  useEffect(() => {
    if (!componentRef.current) return;
    if (selectedIndexData.origin === 'scroll') return;

    const targetElement = findStepElement(componentRef.current, selectedIndexData.value);
    if (!targetElement) return;

    componentRef.current.scroll({ top: targetElement.offsetTop, behavior: 'smooth' });
  }, [componentRef.current, selectedIndexData]);

  return { componentRef };
}
